import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useDASI } from '../../context/DASIContext';
import './styles.scss';

const BrokerResolutionDesk = () => {
  const location = useLocation();
  const { entries, setCurrentIndex } = useDASI();
  const [selectedEntryIndex, setSelectedEntryIndex] = useState(null);
  const [copyLabel, setCopyLabel] = useState('Copy to Clipboard');
  const emailRef = useRef(null);

  const entriesWithExceptions = entries
    .map((e, i) => ({ ...e, originalIndex: i }))
    .filter((e) => e.exceptions.length > 0);

  useEffect(() => {
    if (location.state?.entryIndex != null) {
      setSelectedEntryIndex(location.state.entryIndex);
      setCurrentIndex(location.state.entryIndex);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSelectEntry = (originalIndex) => {
    setSelectedEntryIndex(originalIndex);
    setCurrentIndex(originalIndex);
  };

  const getEmailContent = (entry) => {
    const activeEx = entry.exceptions.filter((ex) => !ex.cleared);
    if (activeEx.length === 0) return null;

    let content = `<strong>To:</strong> ${entry.emailTo}<br>` +
      `<strong>Subject:</strong> ${entry.emailSubject}<br><br>` +
      `${entry.emailIntro}<br><br>`;

    activeEx.forEach((ex) => { content += ex.emailSnippet; });
    if (entry.emailOutro) content += entry.emailOutro;
    return content;
  };

  const selectedEntry = selectedEntryIndex !== null ? entries[selectedEntryIndex] : null;
  const emailContent = selectedEntry ? getEmailContent(selectedEntry) : null;

  const handleCopy = () => {
    if (emailRef.current) {
      const text = emailRef.current.innerText;
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy to Clipboard'), 2000);
  };

  return (
    <Box className="td-resolution-wrapper">
      <Typography sx={{ marginTop: 0, marginBottom: '8px', fontWeight: 700, fontSize: '24px', color: '#1e293b' }}>
        Broker Resolution Desk
      </Typography>
      <Typography sx={{ color: '#64748b', fontSize: '14px', marginBottom: '24px', maxWidth: '800px' }}>
        Select an entry from the QA Radar to generate the exact Post-Summary Correction (PSC) instructions
        for your broker.
      </Typography>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        {/* Left: Actionable PSCs queue */}
        <Box className="td-res-queue-panel">
          <Box className="td-res-queue-header">Actionable PSCs</Box>
          {entriesWithExceptions.length === 0 ? (
            <Typography sx={{ padding: '16px', fontSize: '13px', color: '#64748b' }}>
              No actionable PSCs found.
            </Typography>
          ) : (
            entriesWithExceptions.map((e) => (
              <Box
                key={e.id}
                className={`td-res-queue-item${selectedEntryIndex === e.originalIndex ? ' td-res-selected' : ''}`}
                onClick={() => handleSelectEntry(e.originalIndex)}
              >
                ⚠️ {e.id}
                <br />
                <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#64748b' }}>
                  {e.broker}
                </span>
              </Box>
            ))
          )}
        </Box>

        {/* Right: Email / PSC content */}
        <Box sx={{ flex: 1 }}>
          <Box className="td-email-box">
            <button className="td-copy-btn" onClick={handleCopy}>
              {copyLabel}
            </button>
            <Box ref={emailRef}>
              {selectedEntry === null ? (
                <em style={{ color: '#64748b' }}>
                  Select an entry from the left panel to generate PSC instructions.
                </em>
              ) : emailContent === null ? (
                <em>
                  <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓</span>{' '}
                  No PSC required for this entry. All compliance vectors successfully validated.
                </em>
              ) : (
                // emailSnippet content is defined in our own static data — not user input
                // eslint-disable-next-line react/no-danger
                <span dangerouslySetInnerHTML={{ __html: emailContent }} />
              )}
            </Box>
          </Box>

          <Box sx={{ marginTop: '24px', textAlign: 'right' }}>
            <button className="td-primary-btn td-btn-green">
              ⬇ Download PSC Extract (.CSV)
            </button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BrokerResolutionDesk;
